"use client";

import { useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { useLocale, useTranslations } from "next-intl";
import { Send, Check, X, ArrowRight } from "lucide-react";
import { Link } from "@/i18n/navigation";
import {
  parsePhoneNumberFromString,
  AsYouType,
  type CountryCode,
} from "libphonenumber-js";
import { track } from "@/lib/analytics";
import { BACKEND_URL } from "@/lib/config";
import { AppStoreBadge } from "@/components/AppStoreBadge";
import { PlayStoreBadge } from "@/components/PlayStoreBadge";
import type { Platform } from "@/hooks/usePlatform";
import {
  CountrySelect,
  DEFAULT_COUNTRY,
  type Country,
} from "./CountrySelect";

export function SmsModal({
  open,
  onClose,
  platform = "desktop",
  codePartenaire,
  fermable = true,
}: {
  open: boolean;
  onClose: () => void;
  platform?: Platform;
  /**
   * Code du partenaire dont le QR a amené le visiteur (page /r/<code>).
   * Transmis au backend avec le numéro : c'est ce qui rattache ensuite le
   * compte créé dans l'app à ce partenaire, sans que le client saisisse rien.
   */
  codePartenaire?: string;
  /**
   * Sur la page du QR, la modale EST la page : rien à fermer, ni croix, ni
   * clic à côté. Partout ailleurs elle se ferme comme d'habitude.
   */
  fermable?: boolean;
}) {
  const t = useTranslations("send");
  // Cette modale ne concerne plus que l'ordinateur : depuis la sortie de l'app
  // Android le 25 août 2026, iPhone et Android partent directement vers leur
  // magasin (voir SendParcelProvider). Il ne reste donc qu'un seul cas, celui
  // où l'on ignore quel téléphone recevra le lien.
  const locale = useLocale();
  // Tout ce qui n'est pas anglais retombe sur le montage français.
  const suffixeVideo = locale.startsWith("en") ? "en" : "fr";
  const [country, setCountry] = useState<Country>(DEFAULT_COUNTRY);
  const [phone, setPhone] = useState("");
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const parsed = parsePhoneNumberFromString(phone, country.code as CountryCode);
  // isPossible (longueur) plutôt qu'isValid (motif strict) : moins de faux négatifs
  const valid = !!parsed?.isPossible();
  // Numéro E.164 envoyé au serveur ; affichage formaté international
  const e164 = parsed?.number ?? `${country.dial}${phone.replace(/\D/g, "")}`;
  const fullNumber = parsed?.formatInternational() ?? e164;

  // Formatage en direct selon l'indicatif sélectionné
  const handlePhone = (raw: string) => {
    if (raw.length < phone.length) {
      setPhone(raw); // suppression : pas de reformatage (curseur fluide)
    } else {
      setPhone(new AsYouType(country.code as CountryCode).input(raw));
    }
  };

  const handleCountry = (c: Country) => {
    setCountry(c);
    const digits = phone.replace(/\D/g, "");
    setPhone(digits ? new AsYouType(c.code as CountryCode).input(digits) : "");
  };

  const handleClose = () => {
    if (!fermable) return;
    onClose();
    // Reset après la transition de sortie
    setTimeout(() => {
      setSent(false);
      setErrorMsg(null);
      setPhone("");
      setCountry(DEFAULT_COUNTRY);
    }, 300);
  };

  const submit = async () => {
    if (!valid || submitting) return;
    setSubmitting(true);
    setErrorMsg(null);
    try {
      // Envoi direct via le backend Expedys : il gère Twilio, le rate-limit
      // et le lien de téléchargement. Réponse 2xx = succès.
      //
      // Le lien envoyé est expedys.com/app, qui redirige vers le bon magasin
      // selon le téléphone qui l'ouvre. `platform` reste transmis, non plus
      // pour brancher le comportement mais comme contexte du lead.
      const res = await fetch(`${BACKEND_URL}/api/consumer/app-link/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          telephone: e164,
          locale,
          platform,
          ...(codePartenaire ? { code_partenaire: codePartenaire } : {}),
        }),
      });
      if (res.ok) {
        track("sms_link_sent", {
          country: country.code,
          ...(codePartenaire ? { partenaire: codePartenaire } : {}),
        });
        setSent(true);
      } else {
        // 422 region_not_covered : pays pas encore desservi (Twilio geo).
        const data = (await res.json().catch(() => ({}))) as {
          reason?: string;
        };
        setErrorMsg(
          data.reason === "region_not_covered"
            ? t("regionNotCovered")
            : t("error"),
        );
      }
    } catch {
      setErrorMsg(t("error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-espresso/55 p-6 backdrop-blur-[5px]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={handleClose}
        >
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label={t("modalTitle")}
            className="sms-card grid w-full max-w-[760px] grid-cols-[0.85fr_1fr] overflow-hidden rounded-[28px] bg-white shadow-[0_50px_100px_-30px_rgba(0,0,0,.5)] max-[880px]:grid-cols-1"
            initial={{ opacity: 0, y: 14, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 14, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.2, 0.9, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Visuel : le montage de l'app (le même que le hero), l'appareil
                au pixel près et sans marge, donc sans cadre ajouté ici. Le
                rayon suit la largeur d'affichage : 84 / 700 de 196 px. La
                modale ne se monte qu'ouverte, la vidéo ne se charge qu'à ce
                moment-là. */}
            <div className="flex items-center justify-center bg-[radial-gradient(120%_100%_at_30%_0%,#FBF2E9,#F1E2D4)] px-6 py-9 max-[880px]:hidden">
              <div className="w-[196px] animate-float">
                <video
                  key={suffixeVideo}
                  className="block w-full rounded-[24px] shadow-[0_34px_56px_-24px_rgba(28,16,10,.6)]"
                  src={`/apercu-hero-${suffixeVideo}.mp4`}
                  poster={`/apercu-hero-${suffixeVideo}.jpg`}
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="auto"
                  aria-hidden="true"
                />
              </div>
            </div>

            {/* Contenu */}
            <div className="relative px-8 py-9">
              {fermable && (
                <button
                  onClick={handleClose}
                  aria-label={t("close")}
                  className="absolute right-4 top-4 flex h-[34px] w-[34px] items-center justify-center rounded-full border border-line-strong bg-cream-soft text-ink-soft transition hover:bg-cream"
                >
                  <X size={18} />
                </button>
              )}

              {!sent ? (
                <div>
                  <div className="inline-flex items-center gap-2 rounded-full bg-[#F4E5DD] px-3 py-1.5 text-[12.5px] font-bold text-terracotta-dark">
                    <Send size={14} /> {t("modalTitle")}
                  </div>
                  <h3 className="mt-3.5 font-display text-[25px] font-extrabold leading-[1.1] tracking-tight">
                    {t("modalTitle")}
                  </h3>
                  <p className="mt-2 text-[14.5px] leading-[1.55] text-ink-soft">
                    {t("modalSubtitle")}
                  </p>
                  <div className="mt-[18px]">
                    <div className="flex">
                      <CountrySelect value={country} onChange={handleCountry} />
                      <input
                        value={phone}
                        onChange={(e) => handlePhone(e.target.value)}
                        type="tel"
                        inputMode="tel"
                        placeholder={country.example}
                        onKeyDown={(e) => e.key === "Enter" && submit()}
                        className="w-full min-w-0 rounded-r-xl border border-[#E0D5C6] bg-cream-soft px-[15px] py-[13px] text-[15px] text-espresso outline-none transition focus:border-terracotta focus:shadow-[0_0_0_4px_rgba(192,87,61,.14)]"
                      />
                    </div>
                    <button
                      onClick={submit}
                      disabled={!valid || submitting}
                      className="mt-3 flex w-full items-center justify-center gap-2.5 rounded-xl bg-gradient-cta px-5 py-3.5 font-bold text-white shadow-[0_14px_30px_-14px_rgba(192,87,61,.7)] transition enabled:hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Send size={16} /> {t("modalCta")}
                    </button>
                    {errorMsg && (
                      <p className="mt-2.5 text-center text-[13px] font-medium text-[#C0392B]">
                        {errorMsg}
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center py-[18px] text-center">
                  <span className="flex h-[62px] w-[62px] items-center justify-center rounded-full bg-forest-bg text-forest">
                    <Check size={30} />
                  </span>
                  {/* Sur la page du QR, depuis un téléphone, l'action est
                      d'installer : le SMS n'est plus que le filet. */}
                  <h3 className="mt-[18px] font-display text-2xl font-extrabold tracking-tight">
                    {!fermable && platform !== "desktop" ? t("installTitle") : t("successTitle")}
                  </h3>
                  <p className="mt-2 max-w-[300px] text-[14.5px] leading-[1.55] text-ink-soft">
                    {!fermable && platform !== "desktop"
                      ? t("installBody", { phone: fullNumber })
                      : t("successBody", { phone: fullNumber })}
                  </p>
                  {fermable ? (
                    <button
                      onClick={handleClose}
                      className="mt-[22px] rounded-full border border-[#E0D5C6] bg-white px-[22px] py-2.5 text-sm font-bold text-espresso transition hover:bg-cream-soft"
                    >
                      {t("close")}
                    </button>
                  ) : (
                    /* Page du QR : la modale ne se ferme pas, il faut une
                       sortie. Sur téléphone, la personne tient l'appareil où
                       l'app doit s'installer : on ouvre son magasin. (Aucun
                       lien web ne sait ouvrir la boîte de réception des SMS,
                       `sms:` ne fait qu'un nouveau message.) Le numéro est
                       déjà enregistré avec le code du partenaire. Partout,
                       l'accueil du site. */
                    <div className="mt-[22px] flex flex-col items-center gap-2.5">
                      {/* Le badge officiel du magasin détecté : on sait quel
                          téléphone la personne tient. */}
                      {platform === "ios" && <AppStoreBadge />}
                      {platform === "android" && <PlayStoreBadge />}
                      <Link
                        href="/"
                        className="inline-flex items-center gap-1.5 rounded-full border border-[#E0D5C6] bg-white px-[22px] py-2.5 text-sm font-bold text-espresso no-underline transition hover:bg-cream-soft"
                      >
                        {t("discover")} <ArrowRight size={15} />
                      </Link>
                    </div>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
